<template>
  <div>
    <div
      v-if="visible"
      class="financial-document-overlay"
      @click.self="!submitting && handleClose()">
      <div
        role="dialog"
        aria-describedby="financial-document-modal-description"
        aria-labelledby="financial-document-modal-title"
        class="financial-document-dialog"
        @click.stop>
        <button
          type="button"
          class="financial-document-close"
          aria-label="Fechar"
          :disabled="submitting"
          @click="handleClose">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>

        <div v-if="!submitted" class="financial-document-form">
          <div class="financial-document-header">
            <div class="financial-document-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="financial-document-header-icon">
                <rect width="18" height="14" x="3" y="5" rx="2"></rect>
                <line x1="7" x2="11" y1="10" y2="10"></line>
                <line x1="7" x2="9" y1="14" y2="14"></line>
              </svg>
            </div>
            <div class="financial-document-header-text">
              <h2 id="financial-document-modal-title" class="financial-document-title">
                Informe seu CPF ou CNPJ
              </h2>
              <p id="financial-document-modal-description" class="financial-document-subtitle">
                Precisamos do seu documento para gerar a cobrança e seguir com o upgrade.
              </p>
            </div>
          </div>

          <label class="financial-document-label" for="financial-document-input">CPF ou CNPJ</label>
          <div
            class="financial-document-input-group"
            :class="{ 'financial-document-input-group-valid': documentValid, 'financial-document-input-group-invalid': showInvalidState }">
            <span class="financial-document-input-badge">{{ documentLabel }}</span>
            <input
              id="financial-document-input"
              type="text"
              class="financial-document-input"
              placeholder="Digite apenas números"
              :disabled="submitting"
              v-model="maskedDocument"
              @keyup.enter="handleConfirm">
            <span class="financial-document-input-status">
              <svg v-if="documentValid" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="financial-document-status-valid">
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
            </span>
          </div>

          <ErrorAlert
            v-if="error"
            :error="error"
            class="financial-document-error-text" />

          <button
            type="button"
            class="financial-document-confirm-button"
            :disabled="confirmDisabled"
            @click="handleConfirm">
            <span v-if="submitting">Validando...</span>
            <span v-else>Confirmar</span>
          </button>
        </div>

        <div v-else class="financial-document-success">
          <div class="financial-document-success-icon-wrap">
            <svg class="financial-document-success-icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="m8.5 11.5 2.5 2.5 5.5-5.5"></path>
            </svg>
          </div>
          <h2 class="financial-document-title">Documento validado!</h2>
          <p class="financial-document-subtitle">Você já pode seguir com o upgrade.</p>
          <button
            type="button"
            class="financial-document-confirm-button"
            @click="handleContinue">
            Continuar para upgrade
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loopback from '@/services/loopback'
import { EventBus } from '@/services/event-bus'
import { normalizeDocument, detectDocumentType, isValidDocument, maskDocument } from '@/utils/document'

export default {
  name: 'FinancialDocumentModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      documentInput: '',
      submitting: false,
      error: null,
      submitted: false
    }
  },
  computed: {
    documentType () {
      return detectDocumentType(this.documentInput)
    },
    documentLabel () {
      if (this.documentType) return this.documentType
      return '...'
    },
    documentValid () {
      return isValidDocument(this.documentInput)
    },
    showInvalidState () {
      return this.documentInput !== '' && !this.documentValid
    },
    maskedDocument: {
      get () {
        return maskDocument(this.documentInput)
      },
      set (value) {
        this.documentInput = normalizeDocument(value)
      }
    },
    confirmDisabled () {
      return this.submitting || !this.documentValid
    }
  },
  watch: {
    visible (isVisible) {
      if (isVisible) this.resetState()
    }
  },
  methods: {
    resetState () {
      this.documentInput = ''
      this.submitting = false
      this.error = null
      this.submitted = false
    },
    handleClose () {
      this.$emit('close')
    },
    handleContinue () {
      this.$emit('continue')
    },
    // TEL-2243: ramifica por errorCode, nunca pelo texto. O `message` em ingles
    // segue como chave de transicao enquanto nem todo erro do pbxapi manda codigo,
    // mas nunca chega a tela: desconhecido cai no texto padrao daqui.
    mapCreateError (err) {
      const code = (err && err.errorCode) || ''
      const message = (err && typeof err.message === 'string') ? err.message : ''
      if (code === 'FINANCIAL_CPF_CNPJ_IN_USE' || message === 'cpfCnpj already in use') {
        return { lines: ['CPF/CNPJ já está em uso.'], support: true }
      }
      if (['invalid cnpj', 'cpfCnpj: invalid'].includes(message)) {
        return { lines: ['CPF/CNPJ inválido.'] }
      }
      if (code === 'FINANCIAL_CPF_CNPJ_INVALID' || message.includes('invalid cpf')) {
        return { lines: ['CPF inválido ou sem endereço válido cadastrado.'], support: true }
      }
      return { lines: ['Não foi possível criar seus dados financeiros.'], support: true }
    },
    handleConfirm () {
      if (this.confirmDisabled) return
      this.submitting = true
      this.error = null
      loopback
        .post('/accounts/financial', { cpfCnpj: this.documentInput })
        .then(financialDataCreated => {
          if (financialDataCreated) {
            this.$store.commit('auth/setFinancialData', financialDataCreated)
            this.$store.dispatch('auth/loadSoftswitchIntegration', 'softswitch')
            EventBus.$emit('reload-balance')
            this.submitted = true
            this.submitting = false
          } else {
            this.error = { lines: ['Não foi possível criar seus dados financeiros.'], support: true }
            this.submitting = false
          }
        })
        .catch(err => {
          const message = typeof err.message === 'string' ? err.message : ''
          if (message.includes('already activated')) {
            this.proceedWithExistingIntegration()
            return
          }
          this.error = this.mapCreateError(err)
          this.submitting = false
        })
    },
    proceedWithExistingIntegration () {
      loopback
        .get('/accounts/financial')
        .then(financialData => {
          if (financialData) this.$store.commit('auth/setFinancialData', financialData)
          EventBus.$emit('reload-balance')
        })
        .catch(() => {})
        .finally(() => {
          this.submitted = true
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
.financial-document-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.financial-document-dialog {
  position: relative;
  width: 90%;
  max-width: 28rem;
  background: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 1.5rem 1.5rem 1.25rem;
}

.financial-document-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem;
  color: #4b5563;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.financial-document-close:hover {
  color: #1f2937;
}

.financial-document-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.financial-document-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-right: 2rem;
}

.financial-document-icon-wrap {
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #dcfce7;
  display: flex;
  align-items: center;
  justify-content: center;
}

.financial-document-header-icon {
  stroke: #16a34a;
}

.financial-document-header-text {
  flex: 1;
}

.financial-document-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.financial-document-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.financial-document-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.375rem;
}

.financial-document-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.financial-document-input-group:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.financial-document-input-group-valid {
  border-color: #16a34a;
}

.financial-document-input-group-invalid {
  border-color: #dc2626;
}

.financial-document-input-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.25rem;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-right: 1px solid #e5e7eb;
}

.financial-document-input {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.875rem;
  border: none;
  font-size: 0.9375rem;
  color: #1f2937;
  background: #ffffff;
}

.financial-document-input:focus {
  outline: none;
}

.financial-document-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.financial-document-input-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
}

.financial-document-status-valid {
  color: #16a34a;
}

.financial-document-error-text {
  font-size: 0.875rem;
  color: #dc2626;
  margin: 0.75rem 0 0 0;
}

.financial-document-confirm-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 1.25rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #ffffff;
  background-color: #16a34a;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.financial-document-confirm-button:hover {
  background-color: #15803d;
}

.financial-document-confirm-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.9;
}

.financial-document-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem 0 0;
}

.financial-document-success-icon-wrap {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: #dcfce7;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.financial-document-success-icon {
  stroke: #16a34a;
}
</style>



// WEBPACK FOOTER //
// src/components/Subscription/FinancialDocumentModal.vue